import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { getDefaultPermissions } from '../utils/simplePermissions';
// The per-role source of truth — handles all 11 user_role_enum values and applies the
// seat-tier cap. simplePermissions' 3-tier map is still used for the OWNER shortcut only.
import { getEffectivePermissions } from '../utils/employeePermissions';
import { supabase } from '../utils/supabaseClient';
import { showToast } from '../components/Common/Toast';
import DeviceSessionService, { DEVICE_SESSION_OUTCOME } from '../services/DeviceSessionService';
import SecurityService from '../services/SecurityService';
import { getBillingState, TIERS } from '../utils/planUtils';
import { setCompanyTimeZone } from '../utils/dateFormatters';
import settingsService from '../services/SettingsService';
import { isAppOwner, isAdmin } from '../utils/roleUtils';
// No service keys needed - using anon key with proper auth

/**
 * The statuses that revoke a user's access to their company.
 *
 * This list IS `user_company_id()`'s exclusion set, deliberately — that function returns NULL
 * for exactly these, so RLS denies every row the moment a user lands in one of them. The gates
 * below only tested `=== 'inactive'` (board #1240), so a terminated, suspended or retired user
 * still passed login and session restore, got a full app shell, and then found every screen
 * empty because the database was refusing them. An app that loads and shows nothing is a worse
 * answer than a sentence saying why.
 *
 * Keep the two in step: if user_company_id() ever changes its exclusion set, change this.
 */
const ACCESS_REVOKED_STATUSES = ['inactive', 'suspended', 'terminated', 'retired'];

/**
 * Board #1240. Deactivation revokes access at the database: user_company_id() returns NULL for
 * these statuses, so RLS hides the person's OWN users row and the lookup below comes back as
 * "no row" (PGRST116). The status that accessRevokedMessage() needs is exactly what RLS hides,
 * so a deactivated employee was told "User account not found". my_access_status()
 * (migration 20260924_my_access_status) returns only the caller's own status - no data, no
 * access - so the app can say why. Must run BEFORE signOut: it needs the session.
 */
export const revokedStatusForCaller = async () => {
  try {
    const { data } = await supabase.rpc('my_access_status');
    return ACCESS_REVOKED_STATUSES.includes(data) ? data : null;
  } catch {
    return null;
  }
};

/**
 * ProtectedRoute's onboarding check (is_onboarding_complete), started during the start-up
 * check instead of after it (page speed, 2026-10-25): it needs only the company id and the
 * role, both known once the users row is in, and every page waited on it. ProtectedRoute
 * still makes the decision with its own rules; it just takes this answer instead of asking
 * again. Started only when ProtectedRoute's own conditions (isAppOwner / isAdmin, evaluated
 * on the same role fields the session will carry) say it will ask. Taken once, and only
 * while fresh, so any later check goes to the network exactly as before.
 */
const BOOT_ONBOARDING_FRESH_MS = 10000;
let bootOnboardingCheck = null; // { companyId, startedAt, promise }

const startBootOnboardingCheck = (companyId, sessionRoleFields) => {
  bootOnboardingCheck = null;
  if (!companyId || isAppOwner(sessionRoleFields) || !isAdmin(sessionRoleFields)) return;
  const promise = supabase
    .rpc('is_onboarding_complete', { p_company_id: companyId })
    .then((result) => result);
  promise.catch(() => {}); // the taker sees the rejection; an untaken one is not an error
  bootOnboardingCheck = { companyId, startedAt: Date.now(), promise };
};

export const takeBootOnboardingCheck = (companyId) => {
  const entry = bootOnboardingCheck;
  bootOnboardingCheck = null;
  if (!entry || entry.companyId !== companyId) return null;
  if (Date.now() - entry.startedAt > BOOT_ONBOARDING_FRESH_MS) return null;
  return entry.promise;
};

// Shown on the login page after a session-restore sign-out, where there is no thrown error.
const AUTH_NOTICE_KEY = 'authNotice';

// The start-up check's result when it could not ask the server at all (see runCheckAuth).
const UNREACHABLE = 'unreachable';

export const accessRevokedMessage = (status) => {
  switch (status) {
    case 'suspended':
      return 'This account has been suspended. Please contact your administrator.';
    case 'terminated':
      return 'This account has been closed. Please contact your administrator.';
    case 'retired':
      return 'This account has been retired. Please contact your administrator.';
    default:
      return 'This account has been deactivated. Please contact your administrator.';
  }
};

/**
 * Resolve a company's billing state from companies.billing_status — the single source of
 * truth (getBillingState). This used to read the `subscriptions` table, which has zero rows,
 * so every company resolved to free_trial regardless of billing_status; companies already
 * marked 'active' were shown a "Free Trial" badge. billing_status is also what the live
 * marketplace matching RPC gates on, so the app and the server now agree on one column.
 * Returns {tier, state, daysRemaining, reason}. Defensive by design: any error resolves to
 * a trial (the permissive beta default) — never lock someone out because a query failed.
 */
/**
 * An invited employee is created with status 'pending_invite' by admin-invite-user, but
 * NOTHING ever moved them off it — so someone who accepted, set a password and used the app
 * daily still showed "Pending Invite" in the employee list forever. It also matters for
 * money: seats are billed per ACTIVE user, so a permanently-pending employee was a free seat.
 *
 * Reaching this point means they authenticated, which IS acceptance. Flip once, then let
 * the seat count pick them up. Fire-and-forget — never block a login on billing bookkeeping.
 */
const activateAcceptedInvite = async (businessUser) => {
  if (businessUser?.status !== 'pending_invite') return;
  try {
    const { error } = await supabase
      .from('users')
      .update({ status: 'active' })
      .eq('id', businessUser.id);
    if (error) {
      console.error('Could not activate accepted invite:', error.message);
      return;
    }
    businessUser.status = 'active';
    const { default: stripeSubscriptionService } = await import('../services/StripeSubscriptionService');
    stripeSubscriptionService.syncSeats().catch(() => {});
  } catch (e) {
    console.error('Could not activate accepted invite:', e?.message || e);
  }
};

// Billing from an already-read companies row (sign-in reads the row once, through
// SettingsService.getCompanyProfile, and takes the name and billing from the same copy).
const billingFromCompanyRow = (company) => {
  if (!company) return { tier: TIERS.FREE_TRIAL, state: 'trial', daysRemaining: null, reason: null };
  return getBillingState({
    billing_status: company.billing_status,
    trial_end: company.trial_end,
    paid_through: company.paid_through
  });
};

const resolveCompanyBilling = async (companyId) => {
  if (!companyId) return { tier: TIERS.FREE_TRIAL, state: 'trial', daysRemaining: null, reason: null };
  try {
    const { data: company } = await supabase
      .from('companies')
      .select('billing_status, trial_end, paid_through')
      .eq('id', companyId)
      .maybeSingle();

    if (!company) return { tier: TIERS.FREE_TRIAL, state: 'trial', daysRemaining: null, reason: null };

    // No plan/tier lookup: the product has no feature tiers, only trial vs paid vs locked,
    // so billing_status + the two dates decide everything.
    return getBillingState({
      billing_status: company.billing_status,
      trial_end: company.trial_end,
      paid_through: company.paid_through
    });
  } catch {
    return { tier: TIERS.FREE_TRIAL, state: 'trial', daysRemaining: null, reason: null };
  }
};

/**
 * Fetch custom permissions from employee_permissions table
 * @param {string} userId - The user ID to fetch permissions for
 * @param {string} companyId - The company ID for additional filtering
 * @returns {Object|null} - Permissions object or null if not found
 */
const fetchEmployeePermissions = async (userId, companyId) => {
  try {
    const { data, error } = await supabase
      .from('employee_permissions')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      console.warn('Error fetching employee permissions:', error.message);
      return null;
    }

    if (!data) {
      return null;
    }

    // Convert DB row to permissions object (exclude metadata columns)
    const permissionKeys = [
      'dashboard', 'calendar', 'jobs', 'documents', 'customers', 'quotes',
      'invoices', 'incoming_requests', 'employees', 'timesheets', 'payroll',
      'expenses', 'purchase_orders', 'vendors', 'tools', 'inventory',
      'marketplace', 'reports', 'settings'
    ];

    const permissions = {};
    permissionKeys.forEach(key => {
      permissions[key] = data[key] === true;
    });

    return permissions;
  } catch (err) {
    console.warn('Exception fetching permissions:', err);
    return null;
  }
};

/**
 * Merge default permissions with custom database permissions
 * Database permissions override default role-based permissions
 * @param {Object} defaultPermissions - Default permissions from role
 * @param {Object|null} customPermissions - Custom permissions from DB (optional)
 * @returns {Object} - Merged permissions object
 */
const mergePermissions = (defaultPermissions, customPermissions) => {
  if (!customPermissions) {
    return defaultPermissions;
  }

  // Start with defaults, then overlay custom permissions
  const merged = { ...defaultPermissions };

  Object.keys(customPermissions).forEach(key => {
    if (customPermissions[key] !== undefined) {
      merged[key] = customPermissions[key];
    }
  });

  return merged;
};

// Legacy environment variables for beta (will be removed after full Supabase client migration)
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Map database roles to permission system roles
// Database: lowercase enums (technician) -> Frontend: uppercase constants (TECHNICIAN)
const mapDatabaseRoleToPermissionRole = (dbRole) => {
  const roleMap = {
    // Core roles
    'owner': 'OWNER',
    'admin': 'ADMIN',
    'manager': 'ADMIN',
    'dispatcher': 'ADMIN',
    'supervisor': 'ADMIN',

    // Technician roles
    'lead_technician': 'EMPLOYEE',
    'technician': 'EMPLOYEE',
    'apprentice': 'EMPLOYEE',
    'helper': 'EMPLOYEE',

    // Office roles
    'accountant': 'ADMIN',
    'sales_rep': 'ADMIN',
    'customer_service': 'EMPLOYEE',

    // Portal roles
    'customer_portal': 'CUSTOMER',
    'vendor_portal': 'VENDOR',

    // Legacy/special roles
    'app_owner': 'OWNER',
    'employee': 'EMPLOYEE'
  };

  return roleMap[dbRole?.toLowerCase()] || 'EMPLOYEE';
};

/**
 * #5046: record the sign-in in security_audit_log. Reports → Compliance & Audit counts that
 * table as "Security Events — sign-in and account activity", but nothing wrote a sign-in
 * row: App.js only ever calls SecurityService's teardown half (endSession), so the card
 * described coverage that did not exist. The INSERT policy is company_id = user_company_id()
 * (verified live 2026-10-09), so the company must be looked up the same way checkAuth does.
 * user_id is users.id, NOT the auth uid: AuditTrailService resolves the actor's name by
 * users.id, and the two differ on 399 of 407 users (live 2026-10-09).
 * Fire-and-forget: a failed audit write must never affect the sign-in itself.
 */
const logSignIn = async (authUser) => {
  // supabase-js emits SIGNED_IN on every page load and tab refocus for an EXISTING session
  // (_recoverAndRefresh), not just on a real sign-in — that wrote ~800 "User signed in" rows
  // a day for 5 users (#5046 re-test, 2026-10-11). last_sign_in_at only moves on a real
  // credential sign-in, so log once per value of it.
  const authUserId = authUser?.id;
  const signedInAt = authUser?.last_sign_in_at;
  if (!authUserId || !signedInAt) return;
  const seenKey = `tmp_signin_logged_${authUserId}`;
  try {
    if (localStorage.getItem(seenKey) === signedInAt) return;
    localStorage.setItem(seenKey, signedInAt);
  } catch (e) { /* storage unavailable: fall through and log */ }
  try {
    const { data } = await supabase
      .from('users')
      .select('id, company_id')
      .eq('auth_user_id', authUserId)
      .maybeSingle();
    if (!data?.company_id) return;
    await SecurityService.logSecurityEvent(data.id, data.company_id, 'session_started', 'User signed in', 'info');
  } catch (e) {
    console.warn('sign-in audit event not recorded', e?.message);
  }
};

/**
 * Claim this device for the two-device limit once per SIGN-IN, not once per SIGNED_IN event
 * (investigation 2026-10-25-writes-on-page-view.md item 4). supabase-js re-emits SIGNED_IN on
 * every page load and tab refocus (see logSignIn above), and register_device_session
 * re-activates this device's row unconditionally — so a device pushed out by a newer sign-in
 * took its slot back the moment its tab returned to the foreground, evicting the newer device.
 *
 * Keyed on the access token's session_id claim, NOT last_sign_in_at: session_id is minted by a
 * real sign-in and survives token refreshes, while last_sign_in_at is per USER — a pushed-out
 * device's next token refresh brings back the newer device's value and would look like a new
 * sign-in. The key is written only after the server confirmed the claim, so a failed or
 * unreachable register is retried on the next SIGNED_IN exactly as before. No session_id
 * (token unreadable) or no storage: register, as before.
 *
 * A re-emitted SIGNED_IN for the SAME sign-in still re-claims (that is what moves expires_at
 * forward on load/refocus today, and changing the session timeout is not this fix) — unless
 * the server has DEACTIVATED this device's row: displaced by a newer sign-in, or signed out.
 */
const sessionIdOf = (session) => {
  try {
    const payload = String(session?.access_token || '').split('.')[1];
    if (!payload) return null;
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))?.session_id || null;
  } catch (e) {
    return null;
  }
};

const sameUserExceptSession = (a, b) => {
  if (!a || !b) return false;
  const strip = ({ session_expires_at, session_valid, ...rest }) => rest;
  try {
    return JSON.stringify(strip(a)) === JSON.stringify(strip(b));
  } catch (e) {
    return false;
  }
};

const registerDeviceOnSignIn = async (session) => {
  const authUserId = session?.user?.id;
  const sessionId = sessionIdOf(session);
  const key = authUserId && sessionId ? `tmp_device_registered_${authUserId}` : null;
  let sameSignIn = false;
  if (key) {
    try {
      sameSignIn = localStorage.getItem(key) === sessionId;
    } catch (e) { /* storage unavailable: register */ }
  }
  if (sameSignIn && await DeviceSessionService.wasDeviceDeactivated(authUserId)) return;
  const { outcome } = await DeviceSessionService.registerCurrentDevice();
  if (key && outcome === DEVICE_SESSION_OUTCOME.ACTIVE) {
    try { localStorage.setItem(key, sessionId); } catch (e) { /* ignore */ }
  }
};

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

const createDemoUser = async () => {
  try {
    // First create a demo company using Supabase client
    const { data: companyData, error: companyError } = await supabase
      .from('companies')
      .insert({
        name: 'Demo Company',
        address_line1: '123 Business Street',
        city: 'Demo City',
        state_province: 'CA',
        postal_code: '12345',
        phone: '555-123-4567',
        email: 'info@democompany.com'
      })
      .select()
      .single();

    if (companyError) {
      console.error('Error creating demo company:', companyError);
      return null;
    }

    const companyId = companyData?.id;

    // Create demo user using Supabase client
    const { data: userData, error: userError } = await supabase
      .from('users')
      .insert({
        email: 'demo@trademateapp.com',
        first_name: 'Demo',
        last_name: 'User',
        company_id: companyId,
        role: 'OWNER', // Demo user is owner
        status: 'ACTIVE',
        phone: '555-123-4567'
      })
      .select()
      .single();

    if (userError) {
      console.error('Error creating demo user:', userError);
      return null;
    }

    return userData;
  } catch (error) {
    console.error('Error creating demo user:', error);
    return null;
  }
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  // Connection problems: time out and retry (PRODUCT_DECISIONS, 2026-10-23). True when the
  // start-up check could not reach the server, or is still waiting after
  // BOOTSTRAP_STALL_MS, so the app shows "Couldn't connect" with Retry instead of a spinner
  // that never ends. Cleared as soon as a check finishes with an answer.
  const [connectionProblem, setConnectionProblem] = useState(false);

  useEffect(() => {
    if (!loading) return undefined;
    const BOOTSTRAP_STALL_MS = 25000;
    const timer = setTimeout(() => setConnectionProblem(true), BOOTSTRAP_STALL_MS);
    return () => clearTimeout(timer);
  }, [loading]);

  // 🔐 REAL AUTHENTICATION - Auth bypass removed

  // STRICT AUTHENTICATION - No fake fallbacks, Supabase session required
  useEffect(() => {
    // ONE bootstrap per session, not one per auth event (DB-load fix, 2026-10-22). On a page
    // load checkAuth() runs from this effect AND again from the SIGNED_IN that supabase-js
    // emits while recovering the stored session, so every reload read users, profiles,
    // companies, company_tz, settings, employee_permissions and billing twice (measured:
    // each of those requests appeared 2x per reload). A second call for the same signed-in
    // auth user while one is running, or within BOOTSTRAP_REUSE_MS of it finishing, shares
    // that result instead of re-reading. A different user, a sign-out, or a later
    // SIGNED_IN / TOKEN_REFRESHED (tab refocus, hourly refresh) still re-reads as before.
    const BOOTSTRAP_REUSE_MS = 30000;
    let lastBootstrap = null; // { authUserId, promise, settledAt }

    // `event` is the auth event that asked for the check (undefined for the mount call).
    // A SIGNED_IN for the SAME auth user and the SAME sign-in (session_id) is supabase-js
    // re-announcing a session it already had — it does this when the tab regains focus — so
    // a bootstrap that already loaded is reused instead of re-reading everything (data
    // efficiency ruling 1, 2026-11-02). A different user or a new sign-in (new session_id)
    // still bootstraps, and so does TOKEN_REFRESHED.
    const checkAuth = async (event) => {
      let authUserId = null;
      let sessionId = null;
      try {
        const current = (await supabase.auth.getSession()).data?.session;
        authUserId = current?.user?.id || null;
        sessionId = sessionIdOf(current);
      } catch { /* fall through: run the full check, which reports the error */ }
      const prev = lastBootstrap;
      if (authUserId && prev && prev.authUserId === authUserId &&
          (prev.settledAt === null || (prev.loaded && Date.now() - prev.settledAt < BOOTSTRAP_REUSE_MS))) {
        return prev.promise;
      }
      if (event === 'SIGNED_IN' && authUserId && sessionId && prev && prev.loaded &&
          prev.authUserId === authUserId && prev.sessionId === sessionId) {
        return prev.promise;
      }
      const entry = { authUserId, sessionId, promise: null, settledAt: null, loaded: false };
      entry.promise = runCheckAuth().then((loaded) => {
        setConnectionProblem(loaded === UNREACHABLE);
        entry.loaded = loaded === true;
        entry.settledAt = Date.now();
      }, () => { setConnectionProblem(false); entry.settledAt = Date.now(); });
      lastBootstrap = authUserId ? entry : null;
      return entry.promise;
    };

    // Resolves true only when a signed-in session was fully loaded; a failed or signed-out
    // check is never reused, so the next auth event retries it exactly as before.
    const runCheckAuth = async () => {
      let loaded = false;
      try {
        // STRICT: Only check Supabase session, ignore localStorage
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) {
          console.error('Supabase session error:', error);
          // Clean up any stale localStorage data
          localStorage.removeItem('trademate_user');
          setUser(null);
          setIsAuthenticated(false);
          setLoading(false);
          return;
        }

        // STRICT: Must have valid session and user
        if (!session || !session.user) {
          // Clean up any stale localStorage data
          localStorage.removeItem('trademate_user');
          setUser(null);
          setIsAuthenticated(false);
          setLoading(false);
          return;
        }

        // STRICT: Session must not be expired
        if (session.expires_at && session.expires_at < Date.now() / 1000) {
          localStorage.removeItem('trademate_user');
          setUser(null);
          setIsAuthenticated(false);
          setLoading(false);
          return;
        }

        // INDUSTRY STANDARD: Query users + profiles separately (like Jobber, ServiceTitan)
        // Step 1: Get business user record
        const { data: businessUser, error: userError } = await supabase
          .from('users')
          .select('id,company_id,role,status,created_at,updated_at,password_changed_at,must_change_password,first_name,last_name,name,email,phone')
          .eq('auth_user_id', session.user.id)
          .single();

        // A FAILED LOOKUP IS NOT A DENIAL. This cleared the cached identity for ANY
        // error, so a dropped connection, a timeout or a CORS blip signed the contractor
        // out mid-job -- with a session that was still perfectly valid. On a phone in a
        // basement or a van between cells, that is the normal case, not an edge case.
        //
        // PostgREST answers with a `code` (PGRST116 = no row, 42501 = RLS denial). A
        // transport failure has no code, because no server ever replied. Only the former
        // is the server telling us this user may not be here; the latter is us being
        // unable to ask, and the right answer to "I could not ask" is to keep what we
        // already knew and let the next load resolve it.
        const serverAnswered = Boolean(userError?.code) || (!userError && !businessUser);

        if (userError && !serverAnswered) {
          console.warn('Could not reach the server to confirm identity; keeping the cached session.', userError);
          setLoading(false);
          return UNREACHABLE;
        }

        if (userError || !businessUser) {
          const revoked = await revokedStatusForCaller();
          if (revoked) {
            sessionStorage.setItem(AUTH_NOTICE_KEY, accessRevokedMessage(revoked));
            await supabase.auth.signOut();
          } else {
            console.error('Business user record not found:', userError);
          }
          localStorage.removeItem('trademate_user');
          setUser(null);
          setIsAuthenticated(false);
          setLoading(false);
          return;
        }

        // Deactivated employees keep no access. Their business record still
        // exists (it must, to preserve their history), so gate on status here.
        if (ACCESS_REVOKED_STATUSES.includes(businessUser.status)) {
          console.warn(`Account with status "${businessUser.status}" attempted session restore; signing out.`);
          sessionStorage.setItem(AUTH_NOTICE_KEY, accessRevokedMessage(businessUser.status));
          await supabase.auth.signOut();
          localStorage.removeItem('trademate_user');
          setUser(null);
          setIsAuthenticated(false);
          setLoading(false);
          return;
        }

        // Same on session restore — covers an invitee who set a password and landed
        // straight in the app without a fresh login.
        await activateAcceptedInvite(businessUser);

        // Steps 2, 4, 4b, 4c and 5 below each need only the users row, so they are started
        // TOGETHER here and awaited where they used to be (page speed, 2026-10-25). They ran
        // one after another — profiles, companies, company_tz, settings, employee_permissions —
        // and every page waited for all five round trips before asking for its own data.
        // What each step decides, and in what order its result is applied, is unchanged.
        const bootCompanyId = businessUser.company_id;
        const bootDbRole = businessUser.role || 'technician';
        const bootPermissionRole = mapDatabaseRoleToPermissionRole(bootDbRole);
        const bootIsOwner = bootPermissionRole === 'OWNER';
        startBootOnboardingCheck(bootCompanyId, { role: bootPermissionRole, db_role: bootDbRole });
        // A query builder only sends when .then() is called, and sends AGAIN on every call,
        // so each one is turned into a single started promise here.
        const profileRequest = supabase
          .from('profiles')
          .select('avatar_url,preferences,timezone,language,notification_preferences')
          .eq('user_id', businessUser.id)
          .maybeSingle()
          .then((result) => result);
        const companyRequest = bootCompanyId
          ? settingsService.getCompanyProfile(bootCompanyId, { fresh: true })
          : null;
        const tzRequest = bootCompanyId
          ? supabase.rpc('company_tz', { _company_id: bootCompanyId }).then(
              ({ data }) => ({ tz: data }),
              (tzErr) => ({ tzErr })
            )
          : null;
        const settingsRequest = bootCompanyId
          ? settingsService.getBusinessSettings(bootCompanyId, { fresh: true }).then(
              (row) => ({ row }),
              (fmtErr) => ({ fmtErr })
            )
          : null;
        const permissionsRequest = bootIsOwner
          ? null
          : fetchEmployeePermissions(businessUser.id, bootCompanyId);

        // Step 2: Get user profile data (UI preferences only)
        // ✅ FIXED: Use .maybeSingle() instead of .eq() to avoid empty array issues
        const { data: userProfile, error: profileError} = await profileRequest;

        if (profileError) {
          console.warn('Error loading profile:', profileError.message);
        }

        // Step 3: Combine data (industry standard pattern)
        const firstName = businessUser.first_name || '';
        const lastName = businessUser.last_name || '';
        const fullName = businessUser.name || `${firstName} ${lastName}`.trim() || session.user.email?.split('@')[0] || 'User';

        const userData = {
          user_id: businessUser.id,
          company_id: businessUser.company_id,
          email: businessUser.email || session.user.email, // From users table
          role: businessUser.role,
          status: businessUser.status,
          first_name: firstName,
          last_name: lastName,
          full_name: fullName,
          phone: businessUser.phone || '',
          phone_number: businessUser.phone || '', // Alias for compatibility
          avatar_url: userProfile?.avatar_url || '',
          preferences: userProfile?.preferences || {},
          timezone: userProfile?.timezone || 'America/Los_Angeles',
          language: userProfile?.language || 'en',
          notification_preferences: userProfile?.notification_preferences || {},
          // Address fields (from users table)
          address_line_1: businessUser.address_line_1 || '',
          city: businessUser.city || '',
          state_province: businessUser.state_province || '',
          postal_code: businessUser.postal_code || '',
          created_at: businessUser.created_at,
          updated_at: businessUser.updated_at
        };

        // Step 4: Get company name (industry standard)
        // The companies row is read ONCE here, through its owner (SettingsService), fresh,
        // and the same copy gives the billing state below. It also warms the session cache
        // that IntegrationsContext, BrandContext, Invoices and Settings read from; those
        // used to fetch the same row again (4 reads of one row per sign-in, 2026-10-22).
        let companyName = 'Unknown Company';
        let companyRow = null;
        if (userData.company_id) {
          const profile = await companyRequest;
          companyRow = profile && !Array.isArray(profile) ? profile : null;
          companyName = companyRow?.name || 'Unknown Company';

          // Step 4b: Resolve the COMPANY timezone before anything renders a date.
          //
          // dateFormatters.js keeps the active timezone in a module global and its own comment
          // said "Set once when the signed-in user's settings load (see UserContext)" — but
          // UserContext never did it. Measured 2026-08-13: the ONLY caller of
          // setCompanyTimeZone in the whole repo was SettingsService.getBusinessSettings(),
          // which is lazy and cached, so it fired only if some unrelated component happened to
          // request business settings. Until then `companyTimeZone` was undefined and withTz()
          // passed `timeZone: undefined` — i.e. all ~248 supposedly timezone-aware call sites
          // silently rendered in the BROWSER's zone. That fallback is invisible on a dev machine
          // whose browser is already in the company's zone, which is exactly why timezone bugs
          // kept "coming back": the helpers were right, the plumbing was never connected.
          //
          // Uses the company_tz() RPC (migration 20260813f) rather than reading a column, so the
          // fallback chain (company_settings.timezone -> companies.timezone -> default) lives in
          // exactly ONE place instead of being re-expressed here.
          //
          // ORDER: this used to be applied BEFORE the settings read below, which then published
          // settings.timezone over it. The two requests now run together, so the RPC's answer
          // is applied only when the settings read did not publish a row (no row, or the cache
          // was cleared meanwhile so it returned without publishing) — the same final value as
          // before (both answers are company_settings.timezone whenever a settings row exists;
          // live 2026-10-25: 333 of 333 companies, 0 differ).
          const { tz, tzErr } = await tzRequest;
          const { row: settingsRow, fmtErr } = await settingsRequest;
          const settingsPublished = Boolean(settingsRow) && settingsService.cachedBusinessSettings === settingsRow;
          if (tzErr) {
            // Never block sign-in on this. Formatting falls back to browser-local, which is the
            // pre-existing behaviour — but say so, because a silent fallback is what hid this.
            console.error('Could not resolve company timezone; dates will render browser-local:', tzErr);
          } else if (tz && !settingsPublished) {
            setCompanyTimeZone(tz);
          }

          // Board #2579: the date and time FORMAT had the same gap the timezone had above — the
          // only publisher was SettingsService.getBusinessSettings(), so a page that never asked
          // for business settings (Calendar) rendered US month-first for a DD/MM/YYYY company.
          // Published here, at sign-in, beside the timezone - through getBusinessSettings
          // itself (it publishes the formats) rather than a second /settings read: every
          // session made that call moments later anyway, so the settings row was read twice
          // per sign-in (2026-10-22). Never blocks sign-in.
          if (fmtErr) {
            console.error('Could not load company date/time format; using the default:', fmtErr);
          }
        }

        // Step 5: Load custom permissions from database (Phase 3 - Permission Gating)
        // Owners get full access, others get custom permissions merged with defaults
        const dbRole = userData.role || 'technician';
        const permissionRole = mapDatabaseRoleToPermissionRole(dbRole);
        let finalPermissions;

        if (permissionRole === 'OWNER') {
          // Owners always get full access
          finalPermissions = getDefaultPermissions('OWNER');
        } else {
          // Resolve permissions from the RAW db role, not the flattened permissionRole.
          //
          // mapDatabaseRoleToPermissionRole collapses 11 user_role_enum values into
          // OWNER/ADMIN/EMPLOYEE, and simplePermissions.getDefaultPermissions only knows
          // those three. So every non-admin fell into one permissive EMPLOYEE bucket:
          // a technician got marketplace, customers and inventory (operator saw exactly
          // this 2026-08-29), while accountant and sales_rep mapped to ADMIN and got
          // everything. employeePermissions.getDefaultPermissions already handles all 11
          // roles properly and is what the invite/edit UI has always shown — runtime was
          // simply reading a different, cruder map, so what an admin toggled and what the
          // app enforced were two different models.
          //
          // permissionRole is still the session's `role` (below) because the admin/owner
          // route guards key off it; it is only wrong as a PERMISSIONS lookup.
          const customPermissions = await permissionsRequest;
          finalPermissions = getEffectivePermissions(dbRole, customPermissions);
        }

        // Step 6: Create final user session (industry standard structure)
        const billing = billingFromCompanyRow(companyRow);
        const tier = billing.tier;
        const userSession = {
          id: userData.user_id,  // Use business user ID as primary
          user_id: userData.user_id,  // Also include user_id for compatibility
          email: userData.email,
          full_name: userData.full_name,
          first_name: userData.first_name,
          last_name: userData.last_name,
          company_id: userData.company_id,
          company_name: companyName,
          role: permissionRole, // Use mapped role for permissions
          db_role: dbRole, // Keep original database role for reference
          status: userData.status || 'active',
          tier,
          billing, // {tier, state, daysRemaining, reason} — drives the billing banner + gate
          phone: userData.phone,
          phone_number: userData.phone,
          avatar_url: userData.avatar_url,
          timezone: userData.timezone,
          language: userData.language,
          notification_preferences: userData.notification_preferences,
          // profiles.preferences, already read above. ThemeContext takes the saved theme from
          // here instead of reading users + profiles a second time at start-up.
          preferences: userData.preferences,
          address_line_1: userData.address_line_1,
          city: userData.city,
          state_province: userData.state_province,
          postal_code: userData.postal_code,
          permissions: finalPermissions,
          must_change_password: businessUser.must_change_password || false,
          // Store session info for debugging
          session_expires_at: session.expires_at,
          session_valid: true
        };

        localStorage.setItem('trademate_user', JSON.stringify(userSession));
        // Keep the SAME object when nothing a consumer can read changed (a token refresh only
        // moves session_expires_at, which nothing reads). A new object re-fires every effect
        // keyed on `user` across the app.
        setUser((prev) => (sameUserExceptSession(prev, userSession) ? prev : userSession));
        setIsAuthenticated(true);
        loaded = true;

        // TODO: Move seeding to backend RPC (frontend seeding causes 403 errors due to RLS)
        // Seed per-company defaults (idempotent): expense categories, etc.
        // DISABLED: Frontend seeding fails with 403 due to RLS policies on expense_categories
        // This should be handled by backend RPC or during company setup
        // try {
        //   await ensureCompanyDefaults(userSession.company_id);
        // } catch (seedErr) {
        //   console.warn('Seeding defaults skipped:', seedErr);
        // }

      } catch (error) {
        console.error('❌ Auth check failed:', error);
        localStorage.removeItem('trademate_user');
        setUser(null);
        setIsAuthenticated(false);
      }

      setLoading(false);
      return loaded;
    };

    checkAuth();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        lastBootstrap = null;
        setConnectionProblem(false); // signed out is an answer: go to login, not "couldn't connect"
        settingsService.clearCache(); // the next session, maybe another company, starts empty
        localStorage.removeItem('trademate_user');
        setUser(null);
        setIsAuthenticated(false);
      } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        // Re-run auth check to sync user data (a re-announced SIGNED_IN is reused, see checkAuth)
        checkAuth(event);

        // Claim this device for the two-sessions-per-user limit. SIGNED_IN only, not
        // TOKEN_REFRESHED: a refresh fires roughly hourly and re-claiming on each one would
        // be a network round-trip that changes nothing — this device is already the newest.
        // Once per sign-in, not per page load / refocus: see registerDeviceOnSignIn.
        if (event === 'SIGNED_IN') {
          registerDeviceOnSignIn(session);
          logSignIn(session.user);
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  /**
   * Notice when another device has taken this user's slot, and leave cleanly.
   *
   * WARN FIRST, then sign out (operator ruling 2026-09-13). A silent ejection mid-quote reads
   * as a crash and gets filed as a defect instead of prompting the billing conversation it is
   * meant to start, so the toast is shown and the sign-out follows a beat later.
   *
   * Checks on an interval AND on window focus, because the common shape of this is a laptop
   * left open on a desk for hours: the interval catches it while idle, the focus check makes
   * it immediate the moment someone comes back to it.
   *
   * ONLY the REPLACED verdict acts. UNREACHABLE and REFUSED are ignored on purpose — see the
   * header of DeviceSessionService. A tech in a basement keeps working.
   */
  useEffect(() => {
    if (!isAuthenticated) return undefined;

    let cancelled = false;

    const check = async () => {
      // Was this session ended on the SERVER ("Sign out of all devices" on another device, a
      // password change)? The access token stays valid for up to an hour after that, so
      // nothing else notices — and the device-row check below can lose a race: a tab coming
      // back to the foreground re-emits SIGNED_IN, which re-registers the device row. GoTrue
      // answers /user with session_not_found for a revoked session, which auth-js surfaces as
      // AuthSessionMissingError. A network failure is a different error and is ignored, same
      // rule as the device check: only a verdict the server gave signs anyone out.
      let revoked = false;
      try {
        const { error: userError } = await supabase.auth.getUser();
        revoked = userError?.name === 'AuthSessionMissingError';
      } catch { /* unreachable — never sign out on that */ }

      let message = 'You were signed out of this device from another device. Sign in again to continue.';
      if (!revoked) {
        const verdict = await DeviceSessionService.verifyCurrentDevice();
        if (verdict.outcome !== DEVICE_SESSION_OUTCOME.REPLACED) return;
        message = await DeviceSessionService.describeEndedSession();
        if (cancelled) return;
      }
      // Not gated on `cancelled` when revoked: auth-js has already dropped the session, and
      // the SIGNED_OUT that follows unmounts this effect — the user must still be told and
      // moved to /login.

      cancelled = true; // never fire twice while the sign-out is in flight
      showToast('warning', message, 6000);

      // Hand off to the app's EXISTING eject path rather than signing out here. App.js
      // already listens for 'session-expired' and does signOut() plus a hard navigation to
      // /login; calling supabase.auth.signOut() directly only cleared the auth state and
      // left the user sitting on a dashboard that no longer had a session behind it —
      // verified in a browser, where the warning appeared and the page stayed on /dashboard.
      setTimeout(() => {
        window.dispatchEvent(new Event('session-expired'));
      }, 2500);
    };

    // The session timeout counts from the last ACTIVITY on this device, not from sign-in.
    // Activity in any tab moves the shared per-device row forward (at most once a minute),
    // so a background tab is never timed out while the user works in another.
    let lastTouch = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - lastTouch < 60000) return;
      lastTouch = now;
      DeviceSessionService.touchCurrentDevice();
    };
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    activityEvents.forEach((e) => window.addEventListener(e, onActivity, { capture: true, passive: true }));

    const timer = setInterval(check, 60000);
    window.addEventListener('focus', check);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener('focus', check);
      activityEvents.forEach((e) => window.removeEventListener(e, onActivity, { capture: true }));
    };
  }, [isAuthenticated]);

  const login = useCallback(async (email, password) => {
    try {
      // First, authenticate with Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: email,
        password: password
      });

      if (authError) {
        console.error('Supabase Auth Error:', authError);
        throw new Error(`Authentication failed: ${authError.message}`);
      }

      if (!authData.session || !authData.user) {
        console.error('No session returned from Supabase');
        throw new Error('Authentication failed: No session created');
      }

      // INDUSTRY STANDARD: Query users + profiles separately (like Jobber, ServiceTitan)
      // Step 1: Get business user record
      const { data: businessUser, error: userError } = await supabase
        .from('users')
        .select('id,company_id,role,status,created_at,updated_at,password_changed_at,must_change_password,first_name,last_name,name,email,phone')
        .eq('auth_user_id', authData.user.id)
        .single();

      if (userError || !businessUser) {
        // Ask BEFORE signing out - the status lookup needs this session.
        const revoked = await revokedStatusForCaller();
        if (!revoked) console.error('Error fetching business user data:', userError);
        // A revoked account is signed out everywhere. A failed lookup (which can be a
        // network error) ends only this sign-in, not the user's other devices.
        if (revoked) await supabase.auth.signOut();
        else await supabase.auth.signOut({ scope: 'local' });
        throw new Error(revoked
          ? accessRevokedMessage(revoked)
          : 'User account not found. Please contact your administrator.');
      }

      // Deactivated employees cannot log in. Their record is intentionally kept
      // (for historical jobs/timesheets/invoices) so we gate on status, not existence.
      if (ACCESS_REVOKED_STATUSES.includes(businessUser.status)) {
        await supabase.auth.signOut();
        throw new Error(accessRevokedMessage(businessUser.status));
      }

      // Logging in IS accepting the invite — stop showing them as pending, and start
      // counting their seat.
      await activateAcceptedInvite(businessUser);

      // Step 2: Get user profile data (UI preferences only)
      // ✅ FIXED: Use .maybeSingle() instead of .eq() to avoid empty array issues
      const { data: userProfile, error: profileError } = await supabase
        .from('profiles')
        .select('avatar_url,preferences,timezone,language,notification_preferences')
        .eq('user_id', businessUser.id)
        .maybeSingle();

      if (profileError) {
        console.warn('Error loading profile during login:', profileError.message);
      }

      // Step 3: Combine data (industry standard pattern)
      const firstName = businessUser.first_name || '';
      const lastName = businessUser.last_name || '';
      const fullName = businessUser.name || `${firstName} ${lastName}`.trim() || authData.user.email?.split('@')[0] || 'User';

      const loginUserData = {
        user_id: businessUser.id,
        company_id: businessUser.company_id,
        email: authData.user.email, // From auth.users
        role: businessUser.role,
        status: businessUser.status,
        first_name: firstName,
        last_name: lastName,
        full_name: fullName,
        phone: userProfile?.phone || '',
        phone_number: userProfile?.phone || '', // Alias for compatibility
        avatar_url: userProfile?.avatar_url || '',
        preferences: userProfile?.preferences || {},
        timezone: userProfile?.timezone || 'America/Los_Angeles',
        language: userProfile?.language || 'en',
        notification_preferences: userProfile?.notification_preferences || {},
        // Address fields
        address_line_1: userProfile?.address_line_1 || '',
        city: userProfile?.city || '',
        state_province: userProfile?.state_province || '',
        postal_code: userProfile?.postal_code || '',
        created_at: businessUser.created_at,
        updated_at: businessUser.updated_at
      };

      // Step 4: Get company name (industry standard)
      let companyName = 'Unknown Company';
      if (loginUserData.company_id) {
        const { data: company } = await supabase
          .from('companies')
          .select('name')
          .eq('id', loginUserData.company_id)
          .single();
        companyName = company?.name || 'Unknown Company';
      }

      // Step 5: Update last login in users table (business data)
      await supabase
        .from('users')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', loginUserData.user_id);

      // Step 6: Load custom permissions from database (Phase 3 - Permission Gating)
      // Owners get full access, others get custom permissions merged with defaults
      const dbRole = loginUserData.role || 'technician';
      const permissionRole = mapDatabaseRoleToPermissionRole(dbRole);
      let finalPermissions;

      if (permissionRole === 'OWNER') {
        // Owners always get full access
        finalPermissions = getDefaultPermissions('OWNER');
      } else {
        // Resolve from the RAW db role, not the flattened permissionRole — see the note
        // at the other call site. permissionRole stays the session's `role` for the
        // admin/owner route guards; it is only wrong as a PERMISSIONS key.
        const customPermissions = await fetchEmployeePermissions(loginUserData.user_id, loginUserData.company_id);
        finalPermissions = getEffectivePermissions(dbRole, customPermissions);
      }

      // Step 7: Create final user session (industry standard structure)
      const billing = await resolveCompanyBilling(loginUserData.company_id);
      const tier = billing.tier;
      const userSession = {
        id: loginUserData.user_id,  // Use business user ID as primary
        user_id: loginUserData.user_id,  // Also include user_id for compatibility
        email: loginUserData.email,
        full_name: loginUserData.full_name,
        first_name: loginUserData.first_name,
        last_name: loginUserData.last_name,
        company_id: loginUserData.company_id,
        company_name: companyName,
        role: permissionRole, // Use mapped role for permissions
        db_role: dbRole, // Keep original database role for reference
        status: loginUserData.status || 'active',
        tier,
        billing, // {tier, state, daysRemaining, reason} — drives the billing banner + gate
        phone: loginUserData.phone,
        phone_number: loginUserData.phone,
        avatar_url: loginUserData.avatar_url,
        timezone: loginUserData.timezone,
        language: loginUserData.language,
        notification_preferences: loginUserData.notification_preferences,
        address_line_1: loginUserData.address_line_1,
        city: loginUserData.city,
        state_province: loginUserData.state_province,
        postal_code: loginUserData.postal_code,
        permissions: finalPermissions,
        must_change_password: businessUser?.must_change_password || false,
        // Store session info for validation
        session_expires_at: authData.session.expires_at,
        session_valid: true
      };

      localStorage.setItem('trademate_user', JSON.stringify(userSession));
      setUser(userSession);
      setIsAuthenticated(true);

      return userSession;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }, [supabase]);

  // Sign out, then clear this browser's state. Shared by Logout (scope 'local') and
  // Settings → Security "Sign out of all devices" (scope 'global').
  //
  // Operator ruling 2026-10-20 (docs/PRODUCT_DECISIONS.md): Logout ends THIS device only —
  // the industry default (Jobber, HCP, Google). It used to call signOut() with no scope,
  // which is 'global' and silently signed the user out of their phone, tablet and office
  // PC whenever they logged out of any one of them. Scope 'local' still revokes this
  // device's session server-side; it just leaves the other devices alone.
  //
  // The revoked/deactivated-account and password-change sign-outs elsewhere stay global
  // on purpose (Batch 1, 044ae1832).
  const signOutAndClear = useCallback(async (scope) => {
    // Release the device slot(s) while the session still exists — the update needs it.
    await DeviceSessionService.endDeviceSessions({ allDevices: scope === 'global' });

    let signOutError = null;
    try {
      const { error } = await supabase.auth.signOut({ scope });
      if (error) {
        signOutError = error;
        console.error('Supabase signout error:', error);
      }
    } catch (error) {
      signOutError = error;
      console.error('Logout error:', error);
    }

    // Clean up local state (this will also be triggered by auth state change)
    settingsService.clearCache();
    localStorage.removeItem('trademate_user');
    // #1833: page search/filter state outlived logout, so the next person to sign in on this
    // browser opened Closed Jobs already filtered by the last user's search. Filters only —
    // display preferences (theme, sidebar, density, saved calendar views) are kept.
    try {
      Object.keys(localStorage)
        .filter(k => k.startsWith('jobs_history_') || ['jobs_filter', 'cal_tech', 'cal_status', 'cal_assigned'].includes(k))
        .forEach(k => localStorage.removeItem(k));
      sessionStorage.removeItem('jobs_history_selected');
    } catch (e) { /* storage unavailable: nothing persisted to clear */ }
    setUser(null);
    setIsAuthenticated(false);
    return { error: signOutError };
  }, [supabase]);

  const logout = useCallback(() => signOutAndClear('local'), [signOutAndClear]);
  const signOutAllDevices = useCallback(() => signOutAndClear('global'), [signOutAndClear]);

  const updateUser = useCallback((updatedUserData) => {
    const newUserData = { ...user, ...updatedUserData };
    setUser(newUserData);
    localStorage.setItem('trademate_user', JSON.stringify(newUserData));
  }, [user]);

  /**
   * Re-resolve billing state without a re-login. Billing is resolved once at login and cached
   * in the session, so a company that just paid stayed locked out until they signed out and
   * back in — a terrible thing to do to someone immediately after taking their money.
   */
  const refreshBilling = useCallback(async () => {
    if (!user?.company_id) return null;
    const billing = await resolveCompanyBilling(user.company_id);
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, billing, tier: billing.tier };
      try { localStorage.setItem('trademate_user', JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
    return billing;
  }, [user?.company_id]);

  const value = useMemo(() => ({
    user,
    isAuthenticated,
    loading,
    connectionProblem,
    login,
    logout,
    signOutAllDevices,
    updateUser,
    refreshBilling,
    supabase  // Export supabase client for components that need it
  }), [user, isAuthenticated, loading, connectionProblem, login, logout, signOutAllDevices, updateUser, refreshBilling, supabase]);

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;
